/**
 * Configuration API Service
 *
 * API client for metrics configuration endpoints
 */

import { apiClient } from '../lib/api-client';

export interface MetricDefinitionConfig {
  id?: string;
  name: string;
  description: string;
  formula: string;
  unit: string;
  category: 'dora' | 'velocity' | 'quality' | 'efficiency' | 'delivery';
  enabled: boolean;
  thresholds?: {
    good?: number;
    warning?: number;
    critical?: number;
  };
  customProperties?: Record<string, any>;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  version?: number;
}

export interface WorkItemTypeMapping {
  id?: string;
  organizationId: string;
  sourceType: string;
  targetCategory: 'NewDevelopment' | 'Maintenance' | 'TechnicalDebt';
  effortEstimation?: {
    defaultSize: 'XS' | 'S' | 'M' | 'L' | 'XL';
    customEffort?: number;
  };
  businessValue?: number;
  enabled: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CalculationFormulaConfig {
  id?: string;
  metricId: string;
  formula: string;
  variables: Record<string, string>;
  validationRules: string[];
  testCases: FormulaTestCase[];
  enabled: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  version?: number;
}

export interface FormulaTestCase {
  name: string;
  inputs: Record<string, number>;
  expectedOutput: number;
  tolerance?: number;
}

export interface ConfigurationValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  testResults?: FormulaTestResult[];
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
  code: string;
}

export interface FormulaTestResult {
  testCase: string;
  passed: boolean;
  expected: number;
  actual: number;
  error?: string;
}

class ConfigurationApiService {
  /**
   * Get all metric definitions
   */
  async getMetricDefinitions(
    organizationId?: string
  ): Promise<MetricDefinitionConfig[]> {
    const params = organizationId ? { organizationId } : {};
    const response = await apiClient.get<MetricDefinitionConfig[]>(
      '/config/metrics',
      { params }
    );
    return response.data!;
  }

  /**
   * Get specific metric definition
   */
  async getMetricDefinition(id: string): Promise<MetricDefinitionConfig> {
    const response = await apiClient.get<MetricDefinitionConfig>(
      `/config/metrics/${id}`
    );
    return response.data!;
  }

  /**
   * Save metric definition
   */
  async saveMetricDefinition(
    definition: MetricDefinitionConfig
  ): Promise<MetricDefinitionConfig> {
    const response = await apiClient.post<MetricDefinitionConfig>(
      '/config/metrics',
      definition
    );
    return response.data!;
  }

  /**
   * Delete metric definition
   */
  async deleteMetricDefinition(id: string): Promise<void> {
    await apiClient.delete(`/config/metrics/${id}`);
  }

  /**
   * Get work item type mappings
   */
  async getWorkItemMappings(
    organizationId: string
  ): Promise<WorkItemTypeMapping[]> {
    const response = await apiClient.get<WorkItemTypeMapping[]>(
      '/config/work-item-mappings',
      {
        params: { organizationId },
      }
    );
    return response.data!;
  }

  /**
   * Save work item mapping
   */
  async saveWorkItemMapping(
    mapping: WorkItemTypeMapping
  ): Promise<WorkItemTypeMapping> {
    const response = await apiClient.post<WorkItemTypeMapping>(
      '/config/work-item-mappings',
      mapping
    );
    return response.data!;
  }

  /**
   * Delete work item mapping
   */
  async deleteWorkItemMapping(id: string): Promise<void> {
    await apiClient.delete(`/config/work-item-mappings/${id}`);
  }

  /**
   * Get formula configurations
   */
  async getFormulaConfigurations(
    metricId?: string
  ): Promise<CalculationFormulaConfig[]> {
    const params = metricId ? { metricId } : {};
    const response = await apiClient.get<CalculationFormulaConfig[]>(
      '/config/formulas',
      { params }
    );
    return response.data!;
  }

  /**
   * Save formula configuration
   */
  async saveFormulaConfiguration(
    formula: CalculationFormulaConfig
  ): Promise<CalculationFormulaConfig> {
    const response = await apiClient.post<CalculationFormulaConfig>(
      '/config/formulas',
      formula
    );
    return response.data!;
  }

  /**
   * Test formula configuration
   */
  async testFormulaConfiguration(id: string): Promise<FormulaTestResult[]> {
    const response = await apiClient.post<FormulaTestResult[]>(
      `/config/formulas/${id}/test`
    );
    return response.data!;
  }

  /**
   * Generate calculation configuration
   */
  async generateCalculationConfig(organizationId: string): Promise<unknown> {
    const response = await apiClient.get<unknown>(
      '/config/calculation-config',
      {
        params: { organizationId },
      }
    );
    return response.data!;
  }

  /**
   * Validate configuration
   */
  async validateConfiguration(
    organizationId: string
  ): Promise<ConfigurationValidationResult> {
    const response = await apiClient.post<ConfigurationValidationResult>(
      '/config/validate',
      {
        organizationId,
      }
    );
    return response.data!;
  }
}

export const configurationApi = new ConfigurationApiService();
